import { throttle } from 'lodash';
import type { QueryClient } from '@tanstack/react-query';
import { houseDocumentKeys } from './queries/queryKeys.ts';

/**
 * How often the documents list is worth refetching while work is landing. Matched to the document
 * status stream's own five-second cadence: nothing moves the list faster than that, so asking
 * sooner buys nothing and costs a round trip over a list that grows with every upload.
 */
export const DOCUMENT_LIST_REFRESH_INTERVAL_MS = 5_000;

type Refresher = ReturnType<typeof throttle<() => void>>;

/**
 * One pending refresh per house, shared by everything that asks for one — held outside React for
 * the same reason the upload session memory is: a per-mount throttle is several throttles, which is
 * no throttle at all.
 *
 * Keyed by client as well as by house. The app has a single `QueryClient`, but tests build one per
 * render, and a throttle that closed over the first would go on invalidating a client nobody is
 * using while the live one never hears about a refresh. Weak, so a discarded client takes its
 * throttles with it.
 */
const refreshersByClient = new WeakMap<QueryClient, Map<number, Refresher>>();

const refreshersFor = (queryClient: QueryClient): Map<number, Refresher> => {
	const existing = refreshersByClient.get(queryClient);
	if (existing) return existing;

	const created = new Map<number, Refresher>();
	refreshersByClient.set(queryClient, created);
	pendingRefreshers.add(created);
	return created;
};

/** Strong-held only so a reset can cancel timers that would otherwise still fire. */
const pendingRefreshers = new Set<Map<number, Refresher>>();

/**
 * Asks for this house's documents list to be refetched, at most once per interval.
 *
 * Every upload task invalidated the list on its own, so an eighty-eight file drop asked for
 * eighty-eight refetches of a list that grew with each one. The backend answered 429 with a
 * `Retry-After` of five and a half minutes, which the client correctly honoured, and the drop sat
 * dead looking hung — the list refresh starving the uploads it was reporting on.
 *
 * Leading edge, so a single upload still updates the page at once; trailing edge, so the last
 * request in a burst is never the one that gets dropped.
 */
export const refreshDocumentList = (queryClient: QueryClient, houseId: number): void => {
	const refreshers = refreshersFor(queryClient);
	let refresh = refreshers.get(houseId);
	if (!refresh) {
		refresh = throttle(
			() => void queryClient.invalidateQueries({ queryKey: houseDocumentKeys.list(houseId) }),
			DOCUMENT_LIST_REFRESH_INTERVAL_MS,
			{ leading: true, trailing: true },
		);
		refreshers.set(houseId, refresh);
	}
	refresh();
};

/**
 * Module state outlives a test, so each one starts from a house nobody has asked about. Cancels
 * rather than only forgetting: a trailing refresh already scheduled would otherwise fire into the
 * next test against a client that has been torn down.
 */
export const forgetDocumentListRefreshers = (): void => {
	for (const refreshers of pendingRefreshers) {
		for (const refresh of refreshers.values()) refresh.cancel();
		refreshers.clear();
	}
	pendingRefreshers.clear();
};
